import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../../components/AppIcon';
import LeaveApplicationForm from '../../MyLeaves/components/LeaveApplicationForm';
import { Skeleton } from '../../../../components/ui/Skeleton';

// Three placeholder rows shown while leave-request data is being fetched.
// Mirrors the actual leave-request row layout (icon + 2 text lines + status pill).
const LeaveRequestRowSkeleton = () => (
  <div className="border border-border rounded-lg p-4">
    <div className="flex items-start gap-3">
      <Skeleton className="w-8 h-8 rounded-lg" />
      <div className="flex-1 space-y-2">
        <div className="flex items-center gap-2">
          <Skeleton className="h-4 w-28 rounded" />
          <Skeleton className="h-5 w-16 rounded-full" />
        </div>
        <Skeleton className="h-3 w-40 rounded" />
        <Skeleton className="h-3 w-3/4 rounded" />
      </div>
    </div>
  </div>
);

const LEAVE_PREVIEW_COUNT = 3;

import {
  getBalancePerUser,
  getLeaveRequestsPerUser,
  getLeaveTypes,
  leaveRequestApproval,
  leaveRequestUndoApproval,
  requestLeave,
  addLeaveAttachments,
} from '../../../../api/Attendance/leaves';

const LeaveRequestPanel = ({ userId, canRequest = false, canApprove = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const rtl = useSelector((state) => state.user.rtl);
  const titleIconGapClass = rtl ? 'ml-2 mr-0' : 'mr-2 ml-0';
  const inlineSpaceClass = rtl ? 'space-x-2 space-x-reverse' : 'space-x-2';
  const inlineSpace3Class = rtl ? 'space-x-3 space-x-reverse' : 'space-x-3';
  const inlineSpace1Class = rtl ? 'space-x-1 space-x-reverse' : 'space-x-1';

  const [showRequestForm, setShowRequestForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [rejectingRequestId, setRejectingRequestId] = useState(null);
  const [rejectionComment, setRejectionComment] = useState('');
  const [rejectionError, setRejectionError] = useState(null);

  const [leaveTypes, setLeaveTypes] = useState([]);
  const [balances, setBalances] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [loading, setLoading] = useState(false);

  const refresh = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const [typesRes, mineRes, balancesRes] = await Promise.all([
        canRequest ? getLeaveTypes(userId) : Promise.resolve(null),
        getLeaveRequestsPerUser(userId),
        canRequest ? getBalancePerUser(userId) : Promise.resolve(null),
      ]);

      const types = typesRes?.payload || [];
      setLeaveTypes(types);

      setBalances(balancesRes?.payload || []);

      setLeaveRequests(mineRes?.payload?.leaveRequests || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, canRequest]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Approved':
      case 'approved':
        return 'text-green-600 bg-green-50';
      case 'Rejected':
      case 'rejected':
        return 'text-red-600 bg-red-50';
      case 'Pending':
      case 'pending':
        return 'text-orange-600 bg-orange-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getLeaveTypeIcon = () => 'Calendar';

  const handleLeaveSubmit = async (input, files) => {
    setSubmitting(true);
    try {
      const result = await requestLeave(input);
      if (result.errors.length === 0) {
        if (files && files.length > 0) {
          try {
            await addLeaveAttachments({
              leaveRequestId: result.payload.id,
              attachments: files,
            });
          } catch {
            // Best-effort attachment upload
          }
        }
        setShowRequestForm(false);
        refresh();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprovalAction = (requestId, action) => {
    if (!requestId) return;
    if (!canApprove) return;

    const normalizedAction = String(action || '').toLowerCase();
    const id = Number(requestId);

    if (normalizedAction === 'reject') {
      const comment = String(rejectionComment || '').trim();
      if (!comment) {
        setRejectionError(t('Rejection comment is required.'));
        return;
      }

      setLoading(true);
      setRejectionError(null);
      leaveRequestApproval({
        id,
        isApproved: false,
        managerComment: comment,
      })
        .then(() => {
          setRejectingRequestId(null);
          setRejectionComment('');
          return refresh();
        })
        .finally(() => setLoading(false));
      return;
    }

    if (normalizedAction === 'approve') {
      setLoading(true);
      leaveRequestApproval({
        id,
        isApproved: true,
      })
        .then(() => refresh())
        .finally(() => setLoading(false));
      return;
    }

    if (normalizedAction === 'undo') {
      setLoading(true);
      leaveRequestUndoApproval({
        id,
        isApproved: true,
      })
        .then(() => refresh())
        .finally(() => setLoading(false));
    }
  };

  const uiLeaveRequests = useMemo(() => {
    return (leaveRequests || []).map((r) => ({
      id: r.id,
      typeLabel: r.leaveType,
      startDate: r.startDate,
      endDate: r.endDate,
      status: r.status,
      reason: r.comments,
      managerComments: r.managerComments,
    }));
  }, [leaveRequests]);

  const visibleLeaveRequests = uiLeaveRequests.slice(0, LEAVE_PREVIEW_COUNT);
  const hasMoreLeaveRequests = uiLeaveRequests.length > LEAVE_PREVIEW_COUNT;

  return (
    <div className="bg-card border border-border rounded-xl">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-foreground flex items-center">
            <Icon name="CalendarX" size={18} className={titleIconGapClass} />
            {t('Leave Management')}
          </h3>
          {!!userId && canRequest && (
            <button
              onClick={() => setShowRequestForm(true)}
              disabled={loading}
              className={`flex items-center ${inlineSpaceClass} bg-primary text-primary-foreground px-3 py-2 rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors`}
            >
              <Icon name="Plus" size={14} />
              <span>{t('Request Leave')}</span>
            </button>
          )}
        </div>
      </div>
      <div className="p-6">
        {/* Leave Application Form Modal */}
        {canRequest && showRequestForm && (
          <LeaveApplicationForm
            onSubmit={handleLeaveSubmit}
            onCancel={() => setShowRequestForm(false)}
            balances={balances}
            leaveTypes={leaveTypes}
            userId={userId}
            submitting={submitting}
          />
        )}

        {/* Leave Requests List */}
        <div className="space-y-4">
          <h4 className="text-sm font-semibold text-foreground">
            {t('Leave Requests')}
          </h4>

          {loading && uiLeaveRequests.length === 0 && (
            <div className="space-y-3" aria-busy="true" aria-live="polite">
              <LeaveRequestRowSkeleton />
              <LeaveRequestRowSkeleton />
              <LeaveRequestRowSkeleton />
            </div>
          )}

          {visibleLeaveRequests?.map((request) => {
            const statusLower = String(request?.status || '').toLowerCase();
            const canShowApproveReject = canApprove && (statusLower === 'pending');
            const canShowUndo = canApprove && (statusLower === 'approved' || statusLower === 'rejected');
            const isRejectingThis = String(rejectingRequestId || '') === String(request?.id || '');

            return (
            <div key={request?.id} className="border border-border rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className={`flex items-start ${inlineSpace3Class}`}>
                  <div className="w-8 h-8 bg-muted rounded-lg flex items-center justify-center">
                    <Icon name={getLeaveTypeIcon()} size={16} />
                  </div>
                  <div className="flex-1">
                    <div className={`flex items-center ${inlineSpaceClass} mb-1`}>
                      <span className="text-sm font-medium text-foreground">
                        {request?.typeLabel}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(request?.status)}`}>
                        {request?.status ? t(String(request.status)) : ''}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground mb-2">
                      {new Date(request?.startDate)?.toLocaleDateString()} - {new Date(request?.endDate)?.toLocaleDateString()}
                    </div>
                    <p className="text-xs text-foreground">{request?.reason}</p>
                    {!!request?.managerComments && (
                      <p className="text-xs text-muted-foreground mt-2">
                        {t('Manager comment')}: {request?.managerComments}
                      </p>
                    )}
                  </div>
                </div>

                {canShowApproveReject && (
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleApprovalAction(request?.id, 'approve')}
                      disabled={loading}
                      className={`flex items-center ${inlineSpace1Class} bg-green-500/10 text-green-600 px-2 py-1 rounded text-xs hover:bg-green-500/20 transition-colors`}
                    >
                      <Icon name="Check" size={12} />
                      <span>{t('Approve')}</span>
                    </button>
                    <button
                      onClick={() => {
                        setRejectingRequestId(request?.id);
                        setRejectionComment('');
                        setRejectionError(null);
                      }}
                      disabled={loading}
                      className={`flex items-center ${inlineSpace1Class} bg-red-500/10 text-red-600 px-2 py-1 rounded text-xs hover:bg-red-500/20 transition-colors`}
                    >
                      <Icon name="X" size={12} />
                      <span>{t('Reject')}</span>
                    </button>
                  </div>
                )}

                {canShowUndo && (
                  <button
                    onClick={() => handleApprovalAction(request?.id, 'undo')}
                    disabled={loading}
                    className={`flex items-center ${inlineSpace1Class} bg-muted text-foreground px-2 py-1 rounded text-xs hover:bg-muted/80 transition-colors`}
                  >
                    <Icon name="RotateCcw" size={12} />
                    <span>{t('Undo')}</span>
                  </button>
                )}
              </div>

              {isRejectingThis && canApprove && (
                <div className="mt-3 border-t border-border pt-3">
                  <label className="block text-xs font-medium text-foreground mb-2">
                    {t('Rejection comment (required)')}
                  </label>
                  <textarea
                    value={rejectionComment}
                    onChange={(e) => {
                      setRejectionComment(e?.target?.value);
                      if (rejectionError) setRejectionError(null);
                    }}
                    rows={2}
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                    placeholder={t('Explain why this leave request is rejected...')}
                    disabled={loading}
                  />
                  {rejectionError && (
                    <div className="text-xs text-red-600 mt-1">{rejectionError}</div>
                  )}

                  <div className={`flex ${rtl ? 'space-x-2 space-x-reverse' : 'space-x-2'} mt-3`}>
                    <button
                      onClick={() => handleApprovalAction(request?.id, 'reject')}
                      disabled={loading}
                      className={`flex items-center ${inlineSpaceClass} bg-red-600 text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-red-600/90 transition-colors`}
                    >
                      <Icon name="X" size={14} />
                      <span>{t('Submit rejection')}</span>
                    </button>
                    <button
                      onClick={() => {
                        setRejectingRequestId(null);
                        setRejectionComment('');
                        setRejectionError(null);
                      }}
                      disabled={loading}
                      className="px-3 py-2 text-sm border border-border rounded-lg text-foreground hover:bg-muted transition-colors"
                    >
                      {t('Cancel')}
                    </button>
                  </div>
                </div>
              )}
            </div>
            );
          })}

          {hasMoreLeaveRequests && (
            <button
              type="button"
              onClick={() => navigate('/my-leaves')}
              className={`w-full flex items-center justify-center ${inlineSpaceClass} px-3 py-2 text-sm font-medium text-primary border border-border rounded-lg hover:bg-muted transition-colors`}
            >
              <span>{t('View all ({{n}})', { n: uiLeaveRequests.length })}</span>
              <Icon name={rtl ? 'ChevronLeft' : 'ChevronRight'} size={14} />
            </button>
          )}

          {!loading && uiLeaveRequests?.length === 0 && (
            <div className="text-center py-8">
              <Icon name="Calendar" size={32} className="text-muted-foreground/50 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">
                {t('No leave requests')}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeaveRequestPanel;